import React, { useState } from "react";
import { generateTopicFromPrompt } from "../api";

/**
 * Admin-only: create a topic from a free-text prompt using OpenAI.
 *
 * The backend asks the latest OpenAI model to write a full topic (vocab, verbs,
 * expressions, and a scenario), then runs it through the normal import pipeline.
 * The new topic is private until the admin publishes it from the topic list.
 *
 * Props:
 *   onCreated – called after the topic is generated + saved (parent reloads)
 *   onCancel  – close without creating
 */
export default function AITopicCreator({ onCreated, onCancel }) {
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = prompt.trim().length > 0 && !generating;

  async function handleGenerate() {
    if (!canSubmit) return;
    setGenerating(true);
    setError(null);
    try {
      await generateTopicFromPrompt(prompt.trim());
      if (onCreated) await onCreated();
    } catch (e) {
      setError(e.message || "Failed to generate topic.");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div>
      <div className="mb-3 text-xs text-ink-500">
        Describe the topic you want and OpenAI will draft the vocabulary, verbs,
        expressions, and a dialogue. The new topic is{" "}
        <span className="font-semibold">private</span> until you publish it.
      </div>

      <label className="block text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1">
        Prompt
      </label>
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={4}
        maxLength={2000}
        disabled={generating}
        placeholder="e.g. A beginner topic about visiting the doctor, with common symptoms and polite questions."
        className="w-full px-3 py-2 rounded-lg border border-parchment-300 bg-white text-sm text-ink-800 disabled:opacity-60"
      />

      {error && (
        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={handleGenerate}
          disabled={!canSubmit}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          {generating ? "Generating…" : "Generate topic"}
        </button>
        <button
          onClick={onCancel}
          disabled={generating}
          className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 bg-white hover:bg-slate-100 border border-slate-200 transition-colors"
        >
          Cancel
        </button>
        {generating && (
          <span className="text-xs text-ink-400">
            This can take up to a minute…
          </span>
        )}
      </div>
    </div>
  );
}
